import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Copy } from 'lucide-react';
import { tokenUrl, addressUrl, networkOf } from '../lib/networks';
import { absoluteImageUrl } from '../lib/api';
import { shortAddr } from '../lib/wallet';
import { copyToClipboard } from '../lib/clipboard';

const TokenCard = ({ token }) => {
  const net = networkOf(token.chain_id);
  const img = absoluteImageUrl(token.image_url);
  // Stop the outer <Link>'s navigation when clicking sub-actions inside.
  const copy = (e, v, label) => { e.preventDefault(); e.stopPropagation(); copyToClipboard(v, label); };

  return (
    <Link
      to={`/token/${token.contract_address}?chain=${token.chain_id}`}
      data-testid={`token-card-${token.contract_address}`}
      className="cf-card p-5 flex flex-col gap-4 no-underline text-white"
    >
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7DF9FF]/15 to-[#A78BFA]/15 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
          {img ? (
            <img src={img} alt={token.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display font-bold text-xl cf-gradient-text">{token.symbol?.[0] || '?'}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-semibold truncate" data-testid={`token-name-${token.symbol}`}>{token.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-forge-muted font-medium">${token.symbol}</span>
            <span className="cf-chip !py-0 !px-2 !text-[10px]">{net.short}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1.5 text-xs">
        <Row label="Supply" value={Number(token.total_supply).toLocaleString()} />
        <Row label="Contract" value={shortAddr(token.contract_address)} onCopy={(e) => copy(e, token.contract_address, 'Contract')} mono />
        <Row label="Creator" value={shortAddr(token.creator_address)} onCopy={(e) => copy(e, token.creator_address, 'Creator')} mono />
        <div className="flex items-center gap-1.5 pt-1">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#7DF9FF]/10 border border-[#7DF9FF]/25 text-[#7DF9FF] font-medium">Seeded 0.01 ETH</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F472B6]/10 border border-[#F472B6]/25 text-[#F472B6] font-medium">LP burned</span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          data-testid={`view-explorer-${token.symbol}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const url = tokenUrl(token.chain_id, token.contract_address) || addressUrl(token.chain_id, token.contract_address);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
          className="cf-btn-secondary text-xs justify-center flex-1"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Explorer
        </button>
        <span className="cf-btn-primary text-xs !py-2 flex-1 justify-center pointer-events-none">
          View details →
        </span>
      </div>
    </Link>
  );
};

const Row = ({ label, value, onCopy, mono }) => (
  <div className="flex items-center gap-2">
    <span className="text-forge-muted w-16 shrink-0 font-medium">{label}</span>
    <span className={`truncate flex-1 text-white ${mono ? 'font-code' : ''}`}>{value}</span>
    {onCopy && (
      <button onClick={onCopy} className="text-forge-muted hover:text-[#7DF9FF] transition-colors" aria-label="copy"><Copy className="w-3 h-3" /></button>
    )}
  </div>
);

export default TokenCard;
